import type { SortingState } from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useGetAudienceByWorkspace } from '@/api/workspaces/useGetAudienceByWorkspace';
import { Button } from '@/component-library/primitives/Button/Button';
import AudienceLoader from '@/component-library/primitives/Loaders/AudienceLoader';
import { ProvideToasts, Toast, ToastType } from '@/component-library/primitives/Toast/Toast';
import NoDataBoard from '@/component-library/widgets/NoDataBoard/NoDataBoard';
import SearchField from '@/component-library/widgets/SearchField/SearchField';
import Table from '@/component-library/widgets/Table/Table';
import { useStripeFinancialFeaturesEnabled } from '@/hooks/useStripeFinancialFeaturesEnabled';
import { usePageTracking, useTracking } from '@/hooks/useTracking';
import { AppRoutes } from '@/utils/routes/router';

import i18n from '../../../i18n';

import { useAudienceTable } from './useAudienceTable';

/** Shared tab-button styles (used by TabMenu, Settings, etc.). */
export const isActiveButtonClass =
  'border border-custom-lightSilver border-b-white rounded-tl-lg rounded-tr-lg font-medium !text-custom-black';
export const buttonClass =
  'relative z-10 py-2 px-4 mb-[-1px] mr-2 last:mr-0 text-sm text-custom-darkSilver font-light transition-colors border border-custom-lightSilver rounded-tl-lg rounded-tr-lg hover:text-custom-black';

/** Maps TanStack column ids to GET /users/members sortBy / sortDir (paged mode). */
function audienceSortingToQuery(sorting: SortingState): { sortBy: string; sortDir: 'asc' | 'desc' } {
  const first = sorting[0];
  if (!first) return { sortBy: 'email', sortDir: 'asc' };
  const desc = first.desc ?? false;
  switch (first.id) {
    case 'riskScore':
      return { sortBy: 'riskScore', sortDir: desc ? 'desc' : 'asc' };
    case 'engagementLevel':
      return { sortBy: 'engagement', sortDir: desc ? 'desc' : 'asc' };
    case 'email':
      return { sortBy: 'email', sortDir: desc ? 'desc' : 'asc' };
    default:
      return { sortBy: 'email', sortDir: 'asc' };
  }
}

const Audience = (): any => {
  const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);
  const [toastInfo, setToastInfo] = useState({ message: '', toastType: '' });
  const [filterValue, setFilterValue] = useState<string>('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [sorting, setSorting] = useState<SortingState>([]);
  const [riskLevelFilter, setRiskLevelFilter] = useState<'all' | 'low' | 'medium' | 'high'>('all');
  const [statusFilter, setStatusFilter] = useState<
    'all' | 'active' | 'cancelled' | 'past_due' | 'past_customer' | 'free_trial' | 'free' | 'new_user'
  >('all');

  const needsFullMemberList = riskLevelFilter !== 'all' || statusFilter !== 'all';

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(filterValue), 300);
    return () => window.clearTimeout(t);
  }, [filterValue]);

  const { sortBy, sortDir } = useMemo(() => audienceSortingToQuery(sorting), [sorting]);

  useEffect(() => {
    setPagination((p) => ({ ...p, pageIndex: 0 }));
  }, [debouncedSearch, needsFullMemberList, sorting]);

  const audienceQuery = useGetAudienceByWorkspace(
    needsFullMemberList
      ? { mode: 'all' }
      : {
          mode: 'paged',
          pageIndex: pagination.pageIndex,
          pageSize: pagination.pageSize,
          search: debouncedSearch,
          sortBy,
          sortDir,
        },
  );
  const data = audienceQuery.data;
  const isLoading = audienceQuery.isLoading;
  const stripeFinancial = useStripeFinancialFeaturesEnabled();
  const tableHook = useAudienceTable({ serverDrivenSort: !needsFullMemberList });
  const columns = tableHook.columns;
  const deleteConfirmationModal = tableHook.deleteConfirmationModal;
  const t = (key: string) => (i18n as any).t(key) as string;
  const navigate = useNavigate();
  const isActionAllowed = true; //usePermissionChecker(PermissionsEnum.AUDIENCE, HttpMethodsEnum.POST) as boolean;

  const { trackEvent } = useTracking();

  // Track page view
  usePageTracking('audience', { view: 'members' });

  // Track filter changes
  useEffect(() => {
    if (riskLevelFilter !== 'all' || statusFilter !== 'all' || filterValue) {
      trackEvent('audience_filter_applied', {
        riskLevelFilter,
        statusFilter,
        hasSearchQuery: !!filterValue,
      });
    }
  }, [riskLevelFilter, statusFilter, filterValue]); // eslint-disable-line react-hooks/exhaustive-deps

  // Support both 'members' and 'audience' field names for backward compatibility
  const members = data?.members || data?.audience || [];

  const filteredData = useMemo(() => {
    if (!members || members.length === 0) return [];

    if (!needsFullMemberList) {
      return members;
    }

    const textFiltered = members.filter((row: any) =>
      Object.values(row).some(
        (value) =>
          value !== null && value !== undefined && value.toString().toLowerCase().includes(filterValue.toLowerCase()),
      ),
    );

    const getRiskLevelFromScore = (riskScore: number) => {
      if (riskScore >= 60) return 'high';
      if (riskScore >= 30) return 'medium';
      return 'low';
    };

    const getStatusFromRow = (row: any) => {
      const { subscribed, active, stripeCreatedAt, createdAt, parsedChurnRiskData } = row;
      // Get subscription status and engagement level from churnRiskData (actual structure)
      const subscriptionStatus = parsedChurnRiskData?.subscriptionStatus;
      const engagementLevel = parsedChurnRiskData?.engagementLevel;
      const riskScore = parsedChurnRiskData?.riskScore ?? 0;

      const isNewUser = (() => {
        const createdDate = stripeFinancial && stripeCreatedAt ? new Date(stripeCreatedAt * 1000) : new Date(createdAt);
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        return createdDate > sevenDaysAgo && riskScore < 30 && engagementLevel === 'low';
      })();

      if (isNewUser) return 'new_user';
      if (subscriptionStatus === 'active' && subscribed && active) return 'active';
      if (subscriptionStatus === 'canceled' || subscriptionStatus === 'cancelled') return 'cancelled';
      if (subscriptionStatus === 'past_due') return 'past_due';
      if (subscriptionStatus === 'incomplete' || subscriptionStatus === 'incomplete_expired') return 'past_customer';
      if (subscriptionStatus === 'trialing') return 'free_trial';
      return 'free';
    };

    return textFiltered.filter((row: any) => {
      // Apply risk level filter
      if (riskLevelFilter !== 'all') {
        const score = row?.parsedChurnRiskData?.riskScore ?? 0;
        const level = getRiskLevelFromScore(score);
        if (level !== riskLevelFilter) return false;
      }

      // Apply status filter
      if (statusFilter !== 'all') {
        const status = getStatusFromRow(row);
        if (status !== statusFilter) return false;
      }

      return true;
    });
  }, [members, filterValue, riskLevelFilter, statusFilter, stripeFinancial, needsFullMemberList]);

  const handleNavigate = (route: string, isActionAllowed: boolean) => {
    if (!isActionAllowed) {
      setToastInfo({ message: t('CommonErrors.actionNotAllowed'), toastType: 'error' });
      setOpen(true);
      return;
    }
    navigate(route);
  };

  // Close filter dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isFilterDropdownOpen) {
        const target = event.target as Element;
        if (!target.closest('.filter-dropdown-container') && !target.closest('[data-filter-trigger]')) {
          setIsFilterDropdownOpen(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isFilterDropdownOpen]);

  const audienceToolbarButtons = (
    <div className="flex flex-row flex-wrap items-center gap-2 shrink-0">
      <Button
        type="button"
        variant="transparent"
        data-cy="importMembersNavButton"
        data-testid="importMembersNavButton"
        className="!text-sm"
        onClick={() => handleNavigate(AppRoutes.importMembers, isActionAllowed)}
      >
        {t('AudiencePage.buttonOptionImportMembers')}
      </Button>
      <Button
        type="button"
        variant="primary"
        data-cy="createNewMemberButton"
        data-testid="createNewMemberButton"
        className="!text-sm"
        onClick={() => handleNavigate(AppRoutes.createMember, isActionAllowed)}
      >
        {t('AudiencePage.buttonOptionAddMember')}
      </Button>
    </div>
  );

  return (
    <div className="flex flex-col h-full relative pt-4">
      {isLoading ? (
        <AudienceLoader />
      ) : (
        <>
          <div className="flex flex-row flex-wrap items-center gap-3 mb-4">
            <div className="flex-grow min-w-[12rem] justify-start relative" data-filter-trigger>
              <SearchField
                initialValue={filterValue}
                setSearchValue={setFilterValue}
                onFilterClick={() => setIsFilterDropdownOpen((prev) => !prev)}
                filterTooltipContent={
                  <div className="text-sm text-gray-800">
                    <div className="font-semibold mb-1">Searchable columns:</div>
                    <ul className="list-disc list-inside space-y-0.5 text-xs">
                      <li>Name</li>
                      <li>Email</li>
                    </ul>
                  </div>
                }
              />
              {isFilterDropdownOpen && (
                <div className="filter-dropdown-container absolute z-50 top-full left-0 mt-2 bg-white border border-custom-lightSilver rounded-lg shadow-lg p-4 flex flex-col gap-4 min-w-[300px]">
                  <div className="flex items-center gap-3">
                    <label className="text-sm text-custom-darkSilver font-medium">Risk level:</label>
                    <select
                      className="text-sm border border-custom-lightSilver rounded px-3 py-2 bg-white text-custom-darkSilver focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      value={riskLevelFilter}
                      onChange={(e) => setRiskLevelFilter(e.target.value as 'all' | 'low' | 'medium' | 'high')}
                    >
                      <option value="all">All</option>
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                    </select>
                  </div>
                  <div className="flex items-center gap-3">
                    <label className="text-sm text-custom-darkSilver font-medium">Status:</label>
                    <select
                      className="text-sm border border-custom-lightSilver rounded px-3 py-2 bg-white text-custom-darkSilver focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      value={statusFilter}
                      onChange={(e) =>
                        setStatusFilter(
                          e.target.value as
                            | 'all'
                            | 'active'
                            | 'cancelled'
                            | 'past_due'
                            | 'past_customer'
                            | 'free_trial'
                            | 'free'
                            | 'new_user',
                        )
                      }
                    >
                      <option value="all">All</option>
                      <option value="active">Active</option>
                      <option value="cancelled">Cancelled</option>
                      <option value="past_due">Past Due</option>
                      <option value="past_customer">Past Customer</option>
                      <option value="free_trial">Free Trial</option>
                      <option value="free">Free</option>
                      <option value="new_user">New User</option>
                    </select>
                  </div>
                </div>
              )}
            </div>
            {audienceToolbarButtons}
          </div>
          {deleteConfirmationModal}
          {filteredData && filteredData.length > 0 ? (
            <Table
              sortable
              columns={columns}
              data={filteredData ?? []}
              serverSorting={
                needsFullMemberList
                  ? undefined
                  : {
                      sorting,
                      onSortingChange: setSorting,
                    }
              }
              serverPagination={
                needsFullMemberList
                  ? undefined
                  : {
                      pageIndex: pagination.pageIndex,
                      pageSize: pagination.pageSize,
                      totalRowCount: data?.total ?? 0,
                      onPaginationChange: (updater) => {
                        setPagination((prev) => {
                          const next = typeof updater === 'function' ? updater(prev) : updater;
                          return { pageIndex: next.pageIndex, pageSize: next.pageSize };
                        });
                      },
                    }
              }
            />
          ) : (
            <NoDataBoard
              description={t('NoDataBoard.addYourFirstMemberLabel')}
              actionButton={<div className="mt-8">{audienceToolbarButtons}</div>}
            />
          )}
        </>
      )}
      <ProvideToasts>
        <Toast
          isOpen={open}
          setOpen={setOpen}
          message={toastInfo.message}
          toastType={toastInfo.toastType as ToastType}
        />
      </ProvideToasts>
    </div>
  );
};

export default Audience;
